import React, { useState } from 'react';
import {
  ThemeProvider,
  createTheme,
  CssBaseline,
  Container,
  Typography,
  Button,
  Box,
  IconButton,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
} from '@mui/material';
import {
  Brightness4,
  Brightness7,
  Add,
  Done,
  Download,
  Undo,
} from '@mui/icons-material';

// Import components
import MatchDetails from './components/MatchDetails';
import HalfSelector from './components/HalfSelector';
import MatchTimer from './components/MatchTimer';
import SubstitutionList from './components/SubstitutionList';
import MatchNotes from './components/MatchNotes';
import SubstitutionDialog from './components/SubstitutionDialog';
import NotesDialog from './components/NotesDialog';
import DeleteDialog from './components/DeleteDialog';
import API_CONFIG from './config/apiConfig';

function App() {
  // State for theme
  const [darkMode, setDarkMode] = useState(true);

  // State for match details
  const [matchDetails, setMatchDetails] = useState({
    teamA: '',
    teamB: '',
    date: ''
  });

  // State for current substitution
  const [currentSub, setCurrentSub] = useState({
    jerseyIn: '',
    jerseyOut: '',
    team: '',
    time: '',
    half: 1
  });

  // State for list of substitutions
  const [substitutions, setSubstitutions] = useState([]);

  // State for errors
  const [errors, setErrors] = useState({});

  // State for match complete
  const [isComplete, setIsComplete] = useState(false);

  // State for match started
  const [matchStarted, setMatchStarted] = useState(false);

  // State for current half
  const [currentHalf, setCurrentHalf] = useState(1);

  // State for timer (in seconds)
  const [timer, setTimer] = useState(0);

  // State for timer running
  const [timerRunning, setTimerRunning] = useState(false);

  // State for notes
  const [notes, setNotes] = useState([]);

  // State for notes dialog
  const [notesDialogOpen, setNotesDialogOpen] = useState(false);

  // State for current note
  const [currentNote, setCurrentNote] = useState({ time: '', note: '', half: 1 });

  // State for editing index
  const [editingIndex, setEditingIndex] = useState(null);

  // State for editing note index
  const [editingNoteIndex, setEditingNoteIndex] = useState(null);

  // State for dialogs
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteNoteDialogOpen, setDeleteNoteDialogOpen] = useState(false);
  const [deleteIndex, setDeleteIndex] = useState(null);
  const [deleteNoteIndex, setDeleteNoteIndex] = useState(null);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // Timer effect
  React.useEffect(() => {
    let interval;
    if (timerRunning) {
      interval = setInterval(() => {
        setTimer(prev => prev + 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [timerRunning]);

  // Create theme based on darkMode
  const theme = createTheme({
    palette: {
      mode: darkMode ? 'dark' : 'light',
      primary: {
        main: '#1976d2',
      },
      secondary: {
        main: '#ff4081',
      },
    },
  });

  // Toggle theme
  const toggleTheme = () => {
    setDarkMode(!darkMode);
  };

  // Handle match details change
  const handleMatchChange = (e) => {
    const { name, value } = e.target;
    setMatchDetails(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle substitution input change
  const handleSubChange = (e) => {
    const { name, value } = e.target;
    setCurrentSub(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Validate and add substitution
  const addSubstitution = () => {
    const newErrors = {};

    if (!currentSub.jerseyIn.trim()) newErrors.jerseyIn = 'Jersey In is required';
    if (!currentSub.jerseyOut.trim()) newErrors.jerseyOut = 'Jersey Out is required';
    if (!currentSub.team.trim()) newErrors.team = 'Team is required';
    if (!currentSub.time.trim()) newErrors.time = 'Time is required';

    // Check if jersey numbers are valid
    if (currentSub.jerseyIn && (isNaN(currentSub.jerseyIn) || currentSub.jerseyIn < 1 || currentSub.jerseyIn > 99)) {
      newErrors.jerseyIn = 'Jersey number must be between 1 and 99';
    }
    if (currentSub.jerseyOut && (isNaN(currentSub.jerseyOut) || currentSub.jerseyOut < 1 || currentSub.jerseyOut > 99)) {
      newErrors.jerseyOut = 'Jersey number must be between 1 and 99';
    }

    // Check if time is valid
    if (currentSub.time && (isNaN(currentSub.time) || currentSub.time < 0 || currentSub.time > 120)) {
      newErrors.time = 'Time must be between 0 and 120 minutes';
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length === 0) {
      console.log('Adding substitution', currentSub);
      setSubstitutions(prev => {
        const newSubs = [...prev, { ...currentSub }];
        console.log('New substitutions', newSubs);
        return newSubs;
      });
      setCurrentSub({
        jerseyIn: '',
        jerseyOut: '',
        team: '',
        time: ''
      });
      setEditDialogOpen(false);
    }
  };

  // Start match
  const startMatch = () => {
    if (matchDetails.teamA && matchDetails.teamB && matchDetails.date) {
      setMatchStarted(true);
    }
  };

  // Edit substitution
  const editSubstitution = (index) => {
    setCurrentSub(substitutions[index]);
    setEditingIndex(index);
    setEditDialogOpen(true);
  };

  // Update substitution
  const updateSubstitution = () => {
    const newErrors = {};

    if (!currentSub.jerseyIn.trim()) newErrors.jerseyIn = 'Jersey In is required';
    if (!currentSub.jerseyOut.trim()) newErrors.jerseyOut = 'Jersey Out is required';
    if (!currentSub.team.trim()) newErrors.team = 'Team is required';
    if (!currentSub.time.trim()) newErrors.time = 'Time is required';

    if (currentSub.jerseyIn && (isNaN(currentSub.jerseyIn) || currentSub.jerseyIn < 1 || currentSub.jerseyIn > 99)) {
      newErrors.jerseyIn = 'Jersey number must be between 1 and 99';
    }
    if (currentSub.jerseyOut && (isNaN(currentSub.jerseyOut) || currentSub.jerseyOut < 1 || currentSub.jerseyOut > 99)) {
      newErrors.jerseyOut = 'Jersey number must be between 1 and 99';
    }
    if (currentSub.time && (isNaN(currentSub.time) || currentSub.time < 0 || currentSub.time > 120)) {
      newErrors.time = 'Time must be between 0 and 120 minutes';
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length === 0) {
      setSubstitutions(prev => prev.map((sub, i) => i === editingIndex ? { ...currentSub } : sub));
      setCurrentSub({
        jerseyIn: '',
        jerseyOut: '',
        team: '',
        time: ''
      });
      setEditingIndex(null);
      setEditDialogOpen(false);
    }
  };

  // Delete substitution
  const deleteSubstitution = (index) => {
    setDeleteIndex(index);
    setDeleteDialogOpen(true);
  };

  // Confirm delete
  const confirmDelete = () => {
    setSubstitutions(prev => prev.filter((_, i) => i !== deleteIndex));
    setDeleteDialogOpen(false);
    setDeleteIndex(null);
  };

  // Edit note
  const editNote = (index) => {
    setCurrentNote(notes[index]);
    setEditingNoteIndex(index);
    setNotesDialogOpen(true);
  };

  // Update note
  const updateNote = () => {
    setNotes(prev => prev.map((note, i) => i === editingNoteIndex ? { ...currentNote } : note));
    setCurrentNote({ time: '', note: '', half: 1 });
    setEditingNoteIndex(null);
    setNotesDialogOpen(false);
  };

  // Add note
  const addNote = () => {
    if (editingNoteIndex !== null) {
      updateNote();
    } else {
      setNotes(prev => [...prev, { ...currentNote }]);
      setCurrentNote({ time: '', note: '', half: 1 });
      setNotesDialogOpen(false);
    }
  };

  // Delete note
  const deleteNote = (index) => {
    setDeleteNoteIndex(index);
    setDeleteNoteDialogOpen(true);
  };

  // Confirm delete note
  const confirmDeleteNote = () => {
    setNotes(prev => prev.filter((_, i) => i !== deleteNoteIndex));
    setDeleteNoteDialogOpen(false);
    setDeleteNoteIndex(null);
  };

  // Mark match complete
  const markComplete = () => {
    setIsComplete(true);
    setTimerRunning(false);
  };

  // Revert completion
  const revertCompletion = () => {
    setIsComplete(false);
  };

  // Reset all data and go back to match details
  const resetApp = () => {
    setMatchDetails({
      teamA: '',
      teamB: '',
      date: ''
    });
    setSubstitutions([]);
    setNotes([]);
    setCurrentSub({
      jerseyIn: '',
      jerseyOut: '',
      team: '',
      time: '',
      half: 1
    });
    setCurrentNote({ time: '', note: '', half: 1 });
    setMatchStarted(false);
    setCurrentHalf(1);
    setTimer(0);
    setTimerRunning(false);
    setIsComplete(false);
    setEditingIndex(null);
    setEditingNoteIndex(null);
    setShowSuccessDialog(false);
  };

  // Submit data to StepOut API
  const submitData = async () => {
    try {
      const payload = {
        match_object: {
          match: {
            teamA: matchDetails.teamA,
            teamB: matchDetails.teamB,
            matchDate: matchDetails.date,
            competition: null
          },
          substitutions: substitutions.map(sub => ({
            team: sub.team,
            jerseyIn: parseInt(sub.jerseyIn),
            jerseyOut: parseInt(sub.jerseyOut),
            minute: parseInt(sub.time),
            half: sub.half
          })),
          notes: notes.map(note => ({
            minute: parseInt(note.time),
            half: note.half,
            text: note.note
          })),
          meta: {
            source: "csv_upload",
            uploadedAt: new Date().toISOString(),
            version: 1
          }
        }
      };

      console.log('Submitting payload:', payload);

      const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.STORE_MATCH_DATA}`, {
        method: 'POST',
        headers: {
          ...API_CONFIG.HEADERS,
          // 'authorization': API_CONFIG.AUTH.ANALYST_TOKEN,
          'Authorization': API_CONFIG.AUTH.ORGANIZER_TOKEN,
          // 'origin': 'http://tms-beta.stepoutplay.com.s3-website.ap-south-1.amazonaws.com',
          // 'priority': 'u=1, i',
          // 'referer': 'http://tms-beta.stepoutplay.com.s3-website.ap-south-1.amazonaws.com/',
          // 'sec-ch-ua': '"Google Chrome";v="143", "Chromium";v="143", "Not A(Brand";v="24"',
          // 'sec-ch-ua-mobile': '?0',
          // 'sec-ch-ua-platform': '"macOS"',
          // 'sec-fetch-dest': 'empty',
          // 'sec-fetch-mode': 'cors',
          // 'sec-fetch-site': 'cross-site',
          // // 'Access-Control-Allow-Origin': '*',
          // 'user-agent': 'Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/143.0.0.0 Safari/537.36'
        },
        body: JSON.stringify(payload)
      });

      console.log('Response status:', response.status);

      if (response.ok) {
        const responseData = await response.json();
        console.log('Success response:', responseData);
        setShowSuccessDialog(true);
      } else {
        const errorText = await response.text();
        console.error('API Error:', response.status, errorText);
        setErrorMessage(`Failed to submit data. Status: ${response.status}. ${errorText}`);
        setShowErrorDialog(true);
      }
    } catch (error) {
      console.error('Network Error:', error);
      setErrorMessage('Error submitting data. Please check your connection and try again.');
      setShowErrorDialog(true);
    }
  };

  // Export to CSV
  const exportToCSV = () => {
    const csvContent = [
      ['Team A', 'Team B', 'Date'],
      [matchDetails.teamA, matchDetails.teamB, matchDetails.date],
      [],
      ['Jersey In', 'Jersey Out', 'Team', 'Time (min)', 'Half'],
      ...substitutions.map(sub => [sub.jerseyIn, sub.jerseyOut, sub.team, sub.time, sub.half === 1 ? '1st Half' : '2nd Half']),
      [],
      ['Time (min)', 'Half', 'Notes'],
      ...notes.map(note => [note.time, note.half === 1 ? '1st Half' : '2nd Half', note.note])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    const filename = `${matchDetails.teamA.replace(/\s+/g, '_')}_vs_${matchDetails.teamB.replace(/\s+/g, '_')}_${matchDetails.date}_substitutions.csv`;
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {/* Video Background - only show on Match Details screen */}
      {!matchStarted && (
        <Box sx={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', zIndex: -2, overflow: 'hidden' }}>
          <video
            autoPlay
            loop
            muted
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover'
            }}
          >
            <source src="https://match-videos.s3.ap-south-1.amazonaws.com/website-videos/optimized.webm" type="video/webm" />
          </video>
        </Box>
      )}

      {/* Background Blur Overlay - only on Match Details screen */}
      {!matchStarted && (
        <Box sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backdropFilter: 'blur(5px)',
          backgroundColor: 'rgba(0, 0, 0, 0.1)',
          zIndex: -1
        }} />
      )}

      <Container maxWidth="md" sx={{
        py: matchStarted ? 4 : 0,
        minHeight: matchStarted ? '100vh' : '100vh',
        display: 'flex',
        flexDirection: 'column',
        ...(matchStarted ? {} : { p: 0 })
      }}>
        {matchStarted && (
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <img
              src={darkMode ? "https://match-videos.s3.ap-south-1.amazonaws.com/emailer-assets/Logo-white.svg" : "https://match-videos.s3.ap-south-1.amazonaws.com/emailer-assets/Logo-black.svg"}
              alt="StepOut Logo"
              style={{ height: 60, marginBottom: 16 }}
            />
            <Typography variant="h4" component="h1" sx={{ position: 'relative' }}>
              Live Match Tracker Portal
              <IconButton
                sx={{ position: 'absolute', top: 0, right: 0, transform: 'translateX(100%)' }}
                onClick={toggleTheme}
                color="inherit"
              >
                {darkMode ? <Brightness7 /> : <Brightness4 />}
              </IconButton>
            </Typography>
          </Box>
        )}

        {!matchStarted ? (
          <MatchDetails
            matchDetails={matchDetails}
            handleMatchChange={handleMatchChange}
            startMatch={startMatch}
            matchStarted={matchStarted}
          />
        ) : (
          <>
            <MatchDetails
              matchDetails={matchDetails}
              handleMatchChange={handleMatchChange}
              startMatch={startMatch}
              matchStarted={matchStarted}
            />

            <HalfSelector
              currentHalf={currentHalf}
              setCurrentHalf={setCurrentHalf}
            />

            <MatchTimer
              timer={timer}
              currentHalf={currentHalf}
              setTimer={setTimer}
              setCurrentHalf={setCurrentHalf}
              timerRunning={timerRunning}
              setTimerRunning={setTimerRunning}
            />

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => {
                  console.log('Add button clicked');
                  setEditingIndex(null);
                  setCurrentSub({ jerseyIn: '', jerseyOut: '', team: '', time: Math.floor(timer / 60).toString(), half: currentHalf });
                  setErrors({});
                  setEditDialogOpen(true);
                }}
                disabled={isComplete}
                fullWidth
              >
                Add Substitution
              </Button>
              <Button
                variant="outlined"
                onClick={() => {
                  setCurrentNote({ time: Math.floor(timer / 60).toString(), note: '', half: currentHalf });
                  setEditingNoteIndex(null);
                  setNotesDialogOpen(true);
                }}
                disabled={isComplete}
                fullWidth
              >
                Add Notes
              </Button>
            </Box>

            <SubstitutionList
              substitutions={substitutions}
              editSubstitution={editSubstitution}
              deleteSubstitution={deleteSubstitution}
              isComplete={isComplete}
            />

            <MatchNotes
              notes={notes}
              editNote={editNote}
              deleteNote={deleteNote}
              isComplete={isComplete}
            />

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {!isComplete ? (
                <Button
                  variant="contained"
                  startIcon={<Done />}
                  onClick={markComplete}
                  color="success"
                  fullWidth
                >
                  Mark Match Complete
                </Button>
              ) : (
                <>
                  <Button
                    variant="contained"
                    startIcon={<Done />}
                    onClick={() => setShowConfirmDialog(true)}
                    fullWidth
                  >
                    Submit Data
                  </Button>
                  <Box sx={{ mt: 2, mb: 1 }}>
                    <Alert severity="info" sx={{ mb: 1 }}>
                      You can also download the CSV for later reference
                    </Alert>
                    <Button
                      variant="outlined"
                      startIcon={<Download />}
                      onClick={exportToCSV}
                      fullWidth
                    >
                      Download CSV
                    </Button>
                  </Box>
                  <Button
                    variant="outlined"
                    startIcon={<Undo />}
                    onClick={revertCompletion}
                    color="secondary"
                    fullWidth
                  >
                    Revert Completion
                  </Button>
                </>
              )}
            </Box>
          </>
        )}

        {/* Substitution Dialog */}
        <SubstitutionDialog
          open={editDialogOpen}
          onClose={() => setEditDialogOpen(false)}
          currentSub={currentSub}
          handleSubChange={handleSubChange}
          errors={errors}
          editingIndex={editingIndex}
          addSubstitution={addSubstitution}
          updateSubstitution={updateSubstitution}
          matchDetails={matchDetails}
        />

        {/* Notes Dialog */}
        <NotesDialog
          open={notesDialogOpen}
          onClose={() => setNotesDialogOpen(false)}
          currentNote={currentNote}
          setCurrentNote={setCurrentNote}
          addNote={addNote}
          editingIndex={editingNoteIndex}
        />

        {/* Delete Substitution Dialog */}
        <DeleteDialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
          onConfirm={confirmDelete}
          title="Confirm Delete"
          message="Are you sure you want to delete this substitution?"
        />

        {/* Delete Note Dialog */}
        <DeleteDialog
          open={deleteNoteDialogOpen}
          onClose={() => setDeleteNoteDialogOpen(false)}
          onConfirm={confirmDeleteNote}
          title="Confirm Delete"
          message="Are you sure you want to delete this note?"
        />

        {/* Success Dialog */}
        <Dialog open={showSuccessDialog} onClose={() => setShowSuccessDialog(false)} disableEnforceFocus>
          <DialogTitle>Success!</DialogTitle>
          <DialogContent>
            <Typography>
              Match data has been successfully submitted to StepOut database.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setShowSuccessDialog(false);
                resetApp();
              }}
              variant="contained"
              color="success"
            >
              Done
            </Button>
          </DialogActions>
        </Dialog>

        {/* Error Dialog */}
        <Dialog open={showErrorDialog} onClose={() => setShowErrorDialog(false)} disableEnforceFocus>
          <DialogTitle>Error</DialogTitle>
          <DialogContent>
            <Typography>
              {errorMessage}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setShowErrorDialog(false)}
              variant="contained"
              color="primary"
            >
              OK
            </Button>
          </DialogActions>
        </Dialog>

        {/* Confirmation Dialog */}
        <Dialog open={showConfirmDialog} onClose={() => setShowConfirmDialog(false)} disableEnforceFocus>
          <DialogTitle>Confirm Submission</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to submit the match data? Once submitted, the match data cannot edited.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setShowConfirmDialog(false)}
              color="inherit"
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                setShowConfirmDialog(false);
                submitData();
              }}
              variant="contained"
              color="primary"
            >
              Submit
            </Button>
          </DialogActions>
        </Dialog>

        <Box sx={{ mt: 4, textAlign: 'center', py: 2, borderTop: 1, borderColor: 'divider', width: '100%', position: 'relative', zIndex: 1 }}>
          <Typography variant="body2">
            © 2026 StepOut. All rights reserved. <Link href="https://www.stepout.ai" target="_blank" rel="noopener">www.stepout.ai</Link>
          </Typography>
        </Box>
      </Container>
    </ThemeProvider>
  );
}

export default App;